import React from 'react'
import { Box, styled } from '@mui/material'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const StyledDraggableBox = styled(Box)<{
  dragEnabled: boolean
  isDragging: boolean
  cursorStyle: string
}>(({ dragEnabled, isDragging, cursorStyle }) => ({
  position: 'absolute',
  pointerEvents: dragEnabled ? 'auto' : 'none',
  cursor: cursorStyle,
  backgroundColor: 'transparent',
  borderRadius: 1,
  ...(isDragging && dragEnabled && {
    backgroundColor: 'rgba(25, 118, 210, 0.1)',
    border: '2px dashed rgba(25, 118, 210, 0.5)',
    zIndex: 1000,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  }),
}))

const StyledDragLabel = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  padding: '6px 12px',
  borderRadius: 1,
  fontSize: '14px',
  fontWeight: 'bold',
  color: '#1976d2',
  border: '1px solid rgba(25, 118, 210, 0.3)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  whiteSpace: 'nowrap',
  zIndex: 2,
})

interface DraggableGridTileProps {
  id: string
  position: {
    left: string
    top: string
    width: string
    height: string
  }
  label: string
  dragEnabled?: boolean
}

const DraggableGridTile: React.FC<DraggableGridTileProps> = ({ id, position, label, dragEnabled = true }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const getCursorStyle = () => {
    if (!dragEnabled) return 'default'
    return isDragging ? 'grabbing' : 'grab'
  }

  const cursorStyle = getCursorStyle()

  return (
    <StyledDraggableBox
      ref={setNodeRef}
      data-testid={`draggable-tile-${id}`}
      style={{
        ...style,
        left: position.left,
        top: position.top,
        width: position.width,
        height: position.height,
      }}
      dragEnabled={dragEnabled}
      isDragging={isDragging}
      cursorStyle={cursorStyle}
      {...attributes}
      {...(dragEnabled ? listeners : {})}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'transparent',
          cursor: dragEnabled ? cursorStyle : 'default',
          // Add a small visual indicator when hovering to show it's draggable
          '&:hover': dragEnabled ? {
            backgroundColor: 'rgba(25, 118, 210, 0.02)',
          } : {},
        }}
      />
      
      {/* Show label when dragging for visual feedback */}
      {isDragging && dragEnabled && (
        <StyledDragLabel>
          {label}
        </StyledDragLabel>
      )}
    </StyledDraggableBox>
  )
}

export default DraggableGridTile
